import { themeBoxShadow, themeColor } from '@config/theme';
import { ChatTabType, ChatUser, MessageSendStatus } from '@features/chat/types/types';
import { useWindowSize } from '@hooks/useWindowSize';
import { mdiClose, mdiFileDocument, mdiImage, mdiMicrophone, mdiPaperclip, mdiSend } from '@mdi/js';
import Icon from '@mdi/react';
import {
	Box,
	IconButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Typography,
} from '@mui/material';
import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import 'react-quill/dist/quill.snow.css';
import { Attachment, MessageStatus } from 'services/chat';
import { User } from 'services/users';
import { safeBase64Encode } from '@utils/util.functions';
import { monotonicFactory } from 'ulidx';
import { useAttachments, useMessageDraft, useVoiceRecording } from '../hooks';
import { useChatStore } from '../store';
import { useMessageStore } from '../store/messageStore';
import { Message } from '../types/message';
import { MessageContent, MessageContentType } from '../types/messageContent';
import { AttachmentPreview } from './AttachmentPreview';
import ConfirmActionButton from './ConfirmActionButton';
import { RichTextEditor } from './RichTextEditor';
import { VoiceMessagePreview } from './VoiceMessagePreview';
import { VoiceRecorder } from './VoiceRecorder';

interface MessageInputProps {
	channelId: string;
	currentUser: User;
	onMessageSent?: (message: Message) => void;
	editingMessage?: Message | null;
	onCancelEdit?: () => void;
	onSaveEdit?: (messageId: string, contents: any[], attachmentIds: string[]) => Promise<void>;
}

export const MessageInput: React.FC<MessageInputProps> = ({
	channelId,
	currentUser,
	onMessageSent,
	editingMessage,
	onCancelEdit,
	onSaveEdit,
}) => {
	const { t } = useTranslation();
	const { activeTab } = useChatStore();
	const size = useWindowSize();

	// Custom hooks
	const {
		attachments,
		uploadStatus,
		isUploading,
		addAttachments,
		removeAttachment,
		clearAttachments,
		getAttachmentObjects,
		areAllUploaded,
	} = useAttachments();

	const {
		isRecording,
		recordingDuration,
		recordingPaused,
		isPreviewingRecording,
		mediaBlobUrl,
		audioRef,
		formatRecordingTime,
		handleStartRecording,
		handleStopRecording,
		handleCancelRecording,
		handlePreviewRecording,
		processVoiceMessage,
		setOnVoiceMessage,
	} = useVoiceRecording();

	const { debounceSaveDraft, getSavedDraft, clearDraft } = useMessageDraft(channelId);

	// Local state
	const [editorContent, setEditorContent] = useState('');
	const [isSending, setIsSending] = useState(false);
	const [attachmentAnchorEl, setAttachmentAnchorEl] = useState<null | HTMLElement>(null);
	const [mentionedUsers, setMentionedUsers] = useState<ChatUser[]>([]);

	// Add a ref to track sending state more reliably
	const isSendingRef = useRef(false);

	// File input refs
	const fileInputRef = useRef<HTMLInputElement>(null);
	const imageInputRef = useRef<HTMLInputElement>(null);

	// Message sending queue to prevent overwhelming the system
	const messageQueueRef = useRef<Array<{ channelId: string; messageId: string; payload: any }>>([]);
	const isProcessingQueue = useRef(false);

	// Process message queue with throttling
	const processMessageQueue = useCallback(async () => {
		if (isProcessingQueue.current || messageQueueRef.current.length === 0) {
			return;
		}

		isProcessingQueue.current = true;

		while (messageQueueRef.current.length > 0) {
			const { channelId, messageId, payload } = messageQueueRef.current.shift()!;

			try {
				const { sendMessage: zustandSendMessage, updatePendingMessage, removePendingMessage } =
					useMessageStore.getState();
				const result = await zustandSendMessage(channelId, payload);

				if (result) {
					// API call successful - immediately remove from pending and add to confirmed messages
					console.log('✅ Message sent via API, removing from pending:', messageId);
					
					// Store the server response to prevent duplicate processing from WebSocket
					if ((window as any).storeServerResponse) {
						(window as any).storeServerResponse(result);
					}
					
					// Remove from pending messages since we have API confirmation
					removePendingMessage(channelId, messageId);
					
					// Convert API response to the expected format for handleWebSocketMessage
					// The API response may have different field names than WebSocket messages
					const apiMessageForStore = {
						id: result.id,
						channelId: channelId, // Use the channelId from the function parameter, not from result
						content: result.content,
						repliedMessageId: result.repliedMessageId,
						author: result.author,
						mentions: result.mentions || [],
						attachments: result.attachments || [],
						createdAt: result.createdAt,
						status: result.status,
					};
					
					// Add to confirmed messages immediately by calling the store method directly
					// This will trigger React re-renders properly
					useMessageStore.getState().handleWebSocketMessage(apiMessageForStore);
					
					console.log('✅ Message added to confirmed messages via API response:', result.id);
					
				} else {
					// API call failed
					updatePendingMessage(channelId, messageId, MessageSendStatus.Failed);
				}
			} catch (error) {
				console.error('Background send failed:', error);
				// Update message status to failed
				const { updatePendingMessage } = useMessageStore.getState();
				updatePendingMessage(channelId, messageId, MessageSendStatus.Failed);
			}

			// Small delay between sends to prevent overwhelming the server
			if (messageQueueRef.current.length > 0) {
				await new Promise((resolve) => setTimeout(resolve, 50));
			}
		}

		isProcessingQueue.current = false;
	}, []);

	// Load draft when channel changes or component mounts
	useEffect(() => {
		if (!editingMessage) {
			const savedDraft = getSavedDraft();
			setEditorContent(savedDraft);
		}
	}, [channelId, getSavedDraft, editingMessage]);

	// Load editing message content
	useEffect(() => {
		if (editingMessage) {
			const htmlContent = editingMessage.contents
				.filter(
					(content) =>
						content.type === MessageContentType.Text || content.type === MessageContentType.RichText
				)
				.map((content) => {
					if (content.type === MessageContentType.RichText) {
						return content.content;
					} else {
						let html = `<p>${content.content}</p>`;
						if (content.formatting) {
							if (content.formatting.bold) html = `<strong>${html}</strong>`;
							if (content.formatting.italic) html = `<em>${html}</em>`;
							if (content.formatting.underline) html = `<u>${html}</u>`;
						}
						return html;
					}
				})
				.join('');

			setEditorContent(htmlContent);
		} else if (!editingMessage) {
			const savedDraft = getSavedDraft();
			setEditorContent(savedDraft);
		}
	}, [editingMessage, channelId, getSavedDraft]);

	// Set up voice message callback (stable callback)
	useEffect(() => {
		const handleVoiceMessage = async (blob: Blob) => {
			const voiceFile = await processVoiceMessage(blob);
			if (voiceFile) {
				await addAttachments([voiceFile]);
			}
		};

		setOnVoiceMessage(handleVoiceMessage);
	}, []); // Empty dependency array to create stable callback

	// Process rich text content - memoized to avoid repeated processing
	const processRichTextContent = useCallback((html: string) => {
		// Quick early returns for common cases
		if (!html || html === '<p><br></p>' || html === '<p></p>') {
			return '';
		}

		// Single pass with combined regex operations for better performance
		let cleanedHtml = html
			// Remove empty paragraphs from start and end
			.replace(/^((<p><br\s*\/?><\/p>)|(<p>\s*<\/p>))+/g, '')
			.replace(/((<p><br\s*\/?><\/p>)|(<p>\s*<\/p>))+$/g, '')
			// Remove empty list items and lists
			.replace(/<li><br\s*\/?><\/li>|<li>\s*<\/li>/g, '')
			.replace(/<ul>\s*<\/ul>|<ol>\s*<\/ol>/g, '')
			// Add list styling
			.replace(/<ul>/g, '<ul style="list-style-type: disc; padding-left: 1.5em;">')
			.replace(/<ol>/g, '<ol style="list-style-type: decimal; padding-left: 1.5em;">');

		// Note: URL linkification is now handled by RichTextEditor, not here
		// This prevents duplicate processing and loops

		return cleanedHtml.trim();
	}, []);

	// Create message contents
	const createMessageContents = useCallback(
		async (content: string, files: File[]): Promise<MessageContent[]> => {
			const contents: MessageContent[] = [];

			// Add text content if present (even when files are attached)
			if (content && content !== '<p><br></p>') {
				const richContent = processRichTextContent(content);
				contents.push(
					new MessageContent(
						MessageContentType.RichText,
						richContent,
						mentionedUsers.map((user) => ({
							userId: user.id,
							username: user.name,
							displayName: `${user.name} ${user.surname}`,
						})),
						{}
					)
				);
			}

			// Files are handled separately through attachments
			return contents;
		},
		[processRichTextContent, mentionedUsers]
	);

	// Send message
	const sendMessage = useCallback(
		async (content: string, files: File[]) => {
			// Early return check
			const processedContent = processRichTextContent(content);
			const hasContent = processedContent && processedContent.trim() !== '';

			if (!hasContent && files.length === 0) {
				return;
			}

			// Use ref-based guard to prevent concurrent sends
			if (isSendingRef.current) {
				return;
			}

			// Set sending flag immediately
			isSendingRef.current = true;
			setIsSending(true);

			// Capture mentioned users before clearing
			const currentMentionedUsers = [...mentionedUsers];

			// Clear editor and UI state immediately
			setEditorContent('');
			setMentionedUsers([]);

			// Clear draft immediately
			clearDraft();

			// Create message ID and basic message data synchronously
			const ulid = monotonicFactory();
			const messageId = ulid(Date.now());
			const currentTimestamp = Math.floor(Date.now() / 1000);

			const authorDto = {
				id: currentUser.user_id,
				name: currentUser.first_name,
				surname: currentUser.last_name,
				avatar: currentUser.profile_image || '',
			} as ChatUser;

			// Create message contents (lightweight operation)
			const messageContents: MessageContent[] = [];
			if (hasContent) {
				messageContents.push(
					new MessageContent(
						MessageContentType.RichText,
						processedContent,
						currentMentionedUsers.map((user) => ({
							userId: user.id,
							username: user.name,
							displayName: `${user.name} ${user.surname}`,
						})),
						{}
					)
				);
			}

			// Create basic message object
			const message = new Message(
				messageId,
				currentUser.user_id,
				channelId,
				messageContents,
				currentTimestamp,
				authorDto,
				MessageSendStatus.Sending,
				MessageStatus.Active
			);

			// Get attachment objects (this should be fast as they're already uploaded)
			const attachmentObjects = getAttachmentObjects().map((attachment) => ({
				...attachment,
				owner: currentUser.user_id,
			})) as Attachment[];

			// Set attachments on message
			message.setAttachments(attachmentObjects);

			// Add to pending messages immediately using Zustand store
			const { addPendingMessage } = useMessageStore.getState();
			addPendingMessage(channelId, message);

			// Reset sending state
			setIsSending(false);
			isSendingRef.current = false;

			// Defer heavy cleanup operations to not block the UI
			setTimeout(() => {
				// Clean up media blob URL if exists
				if (mediaBlobUrl) {
					URL.revokeObjectURL(mediaBlobUrl);
				}

				// Clear attachments (this can be heavy)
				clearAttachments();
			}, 0);

			// Add to send queue instead of sending immediately
			const attachmentIds = attachmentObjects
				.filter((a) => a && a.s3_key)
				.map((a) => a.s3_key || '');

			// Use safe UTF-8 encoding to handle Unicode characters (including Georgian, Arabic, etc.)
			const messageContentStr = JSON.stringify(message.contents || []);
			const encodedContent = safeBase64Encode(messageContentStr);

			messageQueueRef.current.push({
				channelId,
				messageId: message.id,
				payload: {
					messageId: message.id,
					content: encodedContent,
					attachmentIds: attachmentIds,
				},
			});

			// Process queue (will be throttled automatically)
			processMessageQueue();

			// Notify parent component that message was sent (for UI updates like scrolling)
			if (onMessageSent) {
				onMessageSent(message);
			}
		},
		[
			processRichTextContent,
			currentUser,
			channelId,
			getAttachmentObjects,
			clearAttachments,
			clearDraft,
			processMessageQueue,
			mediaBlobUrl,
			mentionedUsers,
		]
	);

	// Handle editor content change - optimized to reduce unnecessary operations
	const handleEditorChange = useCallback(
		(content: string) => {
			setEditorContent(content);

			// Only save draft for new messages (not when editing)
			if (!editingMessage) {
				debounceSaveDraft(content);
			}
		},
		[editingMessage, debounceSaveDraft]
	);

	// Handle form submit
	const handleSubmit = useCallback(
		async (e: React.FormEvent) => {
			e.preventDefault();

			// Check if we have content or attachments to send
			const hasTextContent =
				editorContent && editorContent !== '<p><br></p>' && editorContent.trim() !== '';

			if (!hasTextContent && attachments.length === 0) {
				return;
			}

			// Prevent multiple submissions with ref-based guard
			if (isSending || isSendingRef.current) {
				return;
			}

			// Additional safety check: Don't send if attachments are still uploading
			if (attachments.length > 0 && !areAllUploaded()) {
				console.log('Cannot send: attachments are still uploading');
				return;
			}

			if (editingMessage && onSaveEdit) {
				const contents = await createMessageContents(editorContent, []);
				const attachmentIds = getAttachmentObjects().map((obj) => obj.s3_key);
				await onSaveEdit(editingMessage.id, contents, attachmentIds);
				setEditorContent('');
				clearAttachments();
				return;
			}

			// Capture current state and send immediately (non-blocking)
			const contentToSend = editorContent;
			const attachmentsToSend = [...attachments];

			// Send message immediately - this now handles all state updates internally
			sendMessage(contentToSend, attachmentsToSend);
		},
		[
			editorContent,
			attachments,
			isSending,
			editingMessage,
			onSaveEdit,
			createMessageContents,
			getAttachmentObjects,
			clearAttachments,
			sendMessage,
		]
	);

	// Handle key down
	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === 'Enter') {
				if (e.shiftKey) {
					// Shift + Enter: Allow default behavior (new line)
					// Don't prevent default - let the editor handle line breaks
					return;
				} else {
					// Enter alone: Send message
					e.preventDefault();
					handleSubmit(e);
				}
			}
		},
		[handleSubmit]
	);

	// Handle file selection
	const handleFileSelect = useCallback(
		async (e: React.ChangeEvent<HTMLInputElement>) => {
			const files = Array.from(e.target.files || []);
			if (files.length === 0) return;

			try {
				await addAttachments(files);
			} catch (error) {
				console.error('Error adding attachments:', error);
			}

			e.target.value = '';
		},
		[addAttachments]
	);

	// Handle attachment menu
	const handleAttachmentClick = (event: React.MouseEvent<HTMLElement>) => {
		setAttachmentAnchorEl(event.currentTarget);
	};

	const handleAttachmentClose = () => {
		setAttachmentAnchorEl(null);
	};

	// Handle menu item clicks and close menu
	const handleImageClick = () => {
		imageInputRef.current?.click();
		handleAttachmentClose();
	};

	const handleFileClick = () => {
		fileInputRef.current?.click();
		handleAttachmentClose();
	};

	// Handle voice message sending
	const handleSendVoiceMessage = useCallback(async () => {
		// Additional guard to prevent duplicate sends
		if (isSending || isSendingRef.current) {
			return;
		}

		await handleSubmit(new Event('submit') as any);
		if (mediaBlobUrl) {
			URL.revokeObjectURL(mediaBlobUrl);
		}
	}, [handleSubmit, mediaBlobUrl, isSending]);

	// Memoize expensive computations to prevent unnecessary re-renders
	const hasTextContent = useMemo(
		() => Boolean(editorContent && editorContent !== '<p><br></p>' && editorContent.trim() !== ''),
		[editorContent]
	);

	const hasAttachments = useMemo(() => attachments.length > 0, [attachments.length]);

	// Check if all attachments are fully uploaded and ready
	const areAttachmentsReady = useMemo(() => {
		if (attachments.length === 0) return true; // No attachments, so ready
		return areAllUploaded(); // All attachments must be uploaded
	}, [attachments.length, areAllUploaded]);

	// Only disable attachment button when uploading (allow text + attachments)
	const isAttachmentDisabled = isUploading;

	// Can send if we have either text content or attachments (or both), AND all attachments are ready
	const canSend = useMemo(() => 
		(hasTextContent || hasAttachments) && areAttachmentsReady, 
		[hasTextContent, hasAttachments, areAttachmentsReady]
	);

	return (
		<Box>
			<Box
				className={classNames('', {
					[activeTab === ChatTabType.Completed && size.width <= 576 ? 'flex' : 'hidden']: true,
				})}
			>
				<ConfirmActionButton />
			</Box>

			<Box
				sx={{
					p: 1,
					backgroundColor: 'background.paper',
					borderTop: '1px solid',
					borderColor: 'divider',
				}}
			>
				{/* Edit mode indicator */}
				{editingMessage && (
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							padding: '4px 8px',
							backgroundColor: 'rgba(0, 0, 0, 0.05)',
							borderTop: '1px solid rgba(0, 0, 0, 0.1)',
						}}
					>
						<Typography variant="body2" color="text.secondary">
							{t('chats.editingMessage')}
						</Typography>
						<IconButton size="small" onClick={onCancelEdit} aria-label="Cancel editing">
							<Icon path={mdiClose} size={0.8} />
						</IconButton>
					</Box>
				)}

				{/* Attachment previews */}
				{attachments.length > 0 && !isRecording && !isPreviewingRecording && (
					<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1, px: 2 }}>
						{attachments.map((file, index) => {
							const fileUploadStatus = uploadStatus.find((status) => status && status.fileName === file.name);
							const isVoiceMessage =
								file.name.startsWith('voice-message-') && file.type.startsWith('audio/');

							if (isVoiceMessage) {
								return (
									<VoiceMessagePreview
										key={`${file.name}-${file.size}`}
										file={file}
										uploadProgress={fileUploadStatus?.progress || 0}
										isComplete={fileUploadStatus ? (fileUploadStatus.isCompleted && !fileUploadStatus.error) : false}
										hasError={fileUploadStatus ? !!fileUploadStatus.error : false}
										onRemove={() => removeAttachment(index)}
										canRemove={true}
									/>
								);
							}

							return (
								<AttachmentPreview
									key={`${file.name}-${file.size}`}
									file={file}
									uploadProgress={fileUploadStatus?.progress || 0}
									isComplete={fileUploadStatus ? (fileUploadStatus.isCompleted && !fileUploadStatus.error) : false}
									hasError={fileUploadStatus ? !!fileUploadStatus.error : false}
									onRemove={() => removeAttachment(index)}
									canRemove={true}
								/>
							);
						})}
					</Box>	
				)}

				{/* Voice Recorder */}
				<VoiceRecorder
					isRecording={isRecording}
					isPreviewingRecording={isPreviewingRecording}
					recordingDuration={recordingDuration}
					recordingPaused={recordingPaused}
					mediaBlobUrl={mediaBlobUrl}
					audioRef={audioRef}
					formatRecordingTime={formatRecordingTime}
					onStartRecording={handleStartRecording}
					onStopRecording={handleStopRecording}
					onCancelRecording={handleCancelRecording}
					onPreviewRecording={handlePreviewRecording}
					onSendVoiceMessage={handleSendVoiceMessage}
				/>

				{/* Main input area */}
				{!isRecording && !isPreviewingRecording && (
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							backgroundColor: 'background.paper',
							borderRadius: '12px',
							p: 1,
						}}
					>
						<Box sx={{ display: 'flex', alignItems: 'center', pl: 1, pr: 0.5 }}>
							<Box sx={{ flex: 1 }}>
								<RichTextEditor
									value={editorContent}
									onChange={handleEditorChange}
									onKeyDown={handleKeyDown}
									placeholder={
										editingMessage
											? t('chats.editYourMessage')
											: hasAttachments
												? t('chats.addMessageToFiles')
												: t('chats.writeMessage')
									}
									disabled={false}
									readOnly={false}
								/>
							</Box>

							{/* Action buttons */}
							<Box sx={{ display: 'flex', alignItems: 'center' }}>
								<IconButton
									onClick={handleAttachmentClick}
									size="small"
									sx={{ color: 'text.secondary' }}
									disabled={isAttachmentDisabled}
								>
									<Icon path={mdiPaperclip} size={0.8} />
								</IconButton>

								<IconButton
									size="small"
									sx={{ color: 'text.secondary' }}
									onClick={handleStartRecording}
									disabled={isAttachmentDisabled || hasAttachments}
								>
									<Icon path={mdiMicrophone} size={0.8} />
								</IconButton>

								<IconButton
									size="small"
									color="primary"
									onClick={handleSubmit}
									disabled={isUploading || isSending || !canSend}
									sx={{ ml: 0.5 }}
								>
									<Icon path={mdiSend} size={0.8} />
								</IconButton>
							</Box>
						</Box>
					</Box>
				)}

				{/* Hidden file inputs */}
				<input
					type="file"
					ref={fileInputRef}
					style={{ display: 'none' }}
					onChange={handleFileSelect}
					multiple
				/>
				<input
					type="file"
					ref={imageInputRef}
					accept="image/*"
					style={{ display: 'none' }}
					onChange={handleFileSelect}
					multiple
				/>

				{/* Attachment Menu */}
				<Menu
					anchorEl={attachmentAnchorEl}
					open={Boolean(attachmentAnchorEl) && !isRecording && !isPreviewingRecording}
					onClose={handleAttachmentClose}
					anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
					transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
					disableAutoFocus
					disableEnforceFocus
					disableRestoreFocus
					sx={{
						'& .MuiPaper-root': {
							borderRadius: '8px',
							background: themeColor.contrastTextWhite,
							boxShadow: themeBoxShadow.primary,
						},
					}}
				>
					<MenuItem onClick={handleImageClick}>
						<ListItemIcon>
							<Icon path={mdiImage} size={0.8} />
						</ListItemIcon>
						<ListItemText>Image</ListItemText>
					</MenuItem>
					<MenuItem onClick={handleFileClick}>
						<ListItemIcon>
							<Icon path={mdiFileDocument} size={0.8} />
						</ListItemIcon>
						<ListItemText>File</ListItemText>
					</MenuItem>
				</Menu>
			</Box>
		</Box>
	);
};
